/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { Body, Container, FlexContainer, H1, Stack, Text, useBreakpointComponent } from "@carrotfertility/carotene"
import { HR } from "../views/atoms/Atoms"
import { SingleColumnLayout } from "../views/atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "../views/atoms/layouts/BodyWithRightSidebarLayout"
import LibraryMarkdown from "./LibraryMarkdown"
import { ICategory, ISubCategory } from "../../contentful-types"
import { Breadcrumbs } from "./shared/Breadcrumbs"
import { VimeoVideo } from "./VimeoVideo"
import { FormattedMessage, useIntl } from "react-intl"
import { carrotDisclaimerMessages } from "utils/CarrotDisclaimers"
// eslint-disable-next-line import/no-restricted-paths
import { ContentfulContentRenderer, ContentfulContentTypes } from "services/contentful"

type LastUpdatedTagProps = {
  lastUpdated: string
  isVideo: boolean
}

function LastUpdatedTag({ lastUpdated, isVideo }: LastUpdatedTagProps): JSX.Element {
  const intl = useIntl()
  const lastUpdatedDate = intl.formatDate(lastUpdated, { year: "numeric", month: "2-digit", day: "2-digit" })
  return (
    <Stack spacing={"12px"} padding={isVideo ? "40px 0px 0px 0px" : "48px 0px 0px 0px"}>
      <Container width={47}>
        <HR width={3} color="black-10" />
      </Container>
      <Body size={"small"} color="black-80">
        <FormattedMessage
          defaultMessage="Last updated: {lastUpdatedDate}"
          description="The date for when the article was last updated"
          values={{ lastUpdatedDate }}
        />
      </Body>
    </Stack>
  )
}

function ArticleLegalSidebar(): JSX.Element {
  const intl = useIntl()
  const dividerBottomSidebar = useBreakpointComponent([
    <Container padding={"48px 0px 24px 0px"} key={"smallest"}>
      <HR color="mineral-grey" />
    </Container>,
    <Container padding={"48px 0px 24px 0px"} key={"small"}>
      <HR color="mineral-grey" />
    </Container>,
    <Container padding={"48px 0px 24px 0px"} key={"medium"}>
      <HR color="mineral-grey" />
    </Container>,
    <Container height={"48px"} key={"large"} />
  ])
  return (
    <>
      {dividerBottomSidebar}
      <FlexContainer>
        <Stack spacing={"medium"} direction={"column"} borderRadius={"8px"} padding={"24px"} background={"lightGrey"}>
          <Text size={"14px"} color={"black80"}>
            {intl.formatMessage(carrotDisclaimerMessages.SERVICES_LEGALITY_AVAILABILITY)}
          </Text>
          <Container width={47}>
            <HR width={3} color={"black-10"} />
          </Container>
          <Text size={"14px"} color={"black80"}>
            {intl.formatMessage(carrotDisclaimerMessages.INCLUSIVE_LANGUAGE)}
          </Text>
        </Stack>
      </FlexContainer>
    </>
  )
}

type MainContentProps = {
  articleBody: string
  articleTitle: string
  isVideo: boolean
  videoUrl: string
  articleContent?: Array<ContentfulContentTypes> | null
}

const MainContent = ({
  articleBody,
  articleContent,
  articleTitle,
  isVideo,
  videoUrl
}: MainContentProps): JSX.Element => {
  if (isVideo) {
    return <VimeoVideo videoUrl={videoUrl} title={articleTitle} />
  } else if (articleContent) {
    return <ContentfulContentRenderer contents={articleContent} />
  } else {
    return <LibraryMarkdown source={articleBody} />
  }
}

type ReadArticleProps = {
  articleBody: string
  articleTitle: string
  category: ICategory
  isVideo: boolean
  lastUpdated: string
  subCategory: ISubCategory
  videoUrl: string
  articleContent?: Array<ContentfulContentTypes> | null
}

export default function ReadLearnArticle({
  articleBody,
  articleTitle,
  category,
  isVideo,
  lastUpdated,
  subCategory,
  videoUrl,
  articleContent
}: ReadArticleProps): JSX.Element {
  const intl = useIntl()
  const dividerSideSidebar = useBreakpointComponent([
    null,
    null,
    null,
    <Container padding={"48px 0px 0px 0px"} key={"large"}>
      <HR color="mineral-grey" />
    </Container>
  ])

  return (
    <SingleColumnLayout header={<Breadcrumbs category={category} subcategory={subCategory} />}>
      <BodyWithRightSidebarLayout sidebar={<ArticleLegalSidebar />}>
        <Container maxWidth={isVideo ? "800px" : "606px"} alignItems={"center"}>
          <H1 stack={"huge"} style={{ fontSize: "40px" }}>
            {articleTitle}
          </H1>
          <MainContent
            articleBody={articleBody}
            articleContent={articleContent}
            articleTitle={articleTitle}
            isVideo={isVideo}
            videoUrl={videoUrl}
          />
          <LastUpdatedTag lastUpdated={lastUpdated} isVideo={isVideo} />
        </Container>
      </BodyWithRightSidebarLayout>
      {dividerSideSidebar}
      <FlexContainer padding={"24px 0px 0px 0px"} maxWidth={"600px"} width={[null, "66%", "100%"]}>
        <Text size={"14px"} color={"black60"}>
          {intl.formatMessage(carrotDisclaimerMessages.CARROT_RESOURCES)}
        </Text>
      </FlexContainer>
    </SingleColumnLayout>
  )
}
